import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { usePartialLeadSave, getPartialLeadId, clearPartialLeadId } from "@/hooks/usePartialLeadSave";
import { useToast } from "@/hooks/use-toast";
import { Phone, CheckCircle, Clock, ShieldCheck, ArrowRight, CalendarCheck } from "lucide-react";

const projectSizes = ["Under $100K", "$100K – $500K", "$500K – $2M", "$2M – $10M", "$10M – $50M", "$50M – $100M", "$100M+"];

const interests = [
  "Turnkey Facility Project",
  "Warehouse Automation",
  "Manufacturing Automation & Robotics",
  "Racking & Storage Systems",
  "Construction & Tenant Improvements",
  "Electrical, Controls & Software",
  "Installations & Rollouts",
  "Relocation / Facility Transition",
  "Equipment & Product Purchase",
  "Sell Us Your Equipment",
  "Maintenance & Service Programs (full program design)",
  "Partnership Opportunity",
  "Something Else / Not Sure Yet",
];

type HeroCallbackFormProps = {
  titleFr?: string;
  subtitleFr?: string;
  /** Preselects the "What do you need?" dropdown (must match an interests value) */
  defaultInterest?: string;
  /** Unique partial-save key per placement so drafts don't collide across pages */
  formKey?: string;
  title?: string;
  subtitle?: string;
};

/**
 * Above-the-fold callback form: converts visitors into call requests without
 * leaving the page. Feeds the same leads pipeline as /book-a-call. Used on the
 * homepage hero and on service landing pages (with a preselected interest).
 */
const HeroCallbackForm = ({
  defaultInterest = "",
  formKey = "home-hero",
  titleFr: _titleFr,
  subtitleFr: _subtitleFr,
  title = "Get a Free Project Call",
  subtitle = "Tell us where to reach you. A specialist calls within one business day to scope your project and give you real numbers.",
}: HeroCallbackFormProps) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    phone: "",
    email: "",
    estimated_budget: "",
    project_description: "",
    interest: defaultInterest,
  });
  usePartialLeadSave({ ...form }, formKey, done);

  const set = (k: keyof typeof form, v: string) => setForm((p) => ({ ...p, [k]: v }));
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email);
  const valid = Boolean(form.first_name && emailValid && form.phone.replace(/\D/g, "").length >= 10);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const data = {
        ...form,
        service_interest: form.interest || "Homepage Call Request",
        project_type: "Book a Call Request",
        source_page: typeof window !== "undefined" ? window.location.pathname : "/",
      };
      await supabase.functions.invoke("save-lead-progress", {
        body: { lead_id: getPartialLeadId(formKey), step: 1, is_complete: true, data },
      });
      supabase.functions.invoke("send-lead-notification", { body: data }).catch(() => {});
      clearPartialLeadId(formKey);
      setDone(true);
    } catch {
      toast({
        title: "Something went wrong",
        description: "Please try again, or call us directly at (305) 433-7789.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white p-8 text-center shadow-2xl">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100">
          <CheckCircle className="h-7 w-7 text-green-600" />
        </div>
        <h3 className="mt-4 text-xl font-bold text-secondary">Done. A specialist will call you.</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Expect our call within one business day. Want to lock in an exact time instead?
        </p>
        <Button asChild className="mt-5 w-full">
          <Link to="/book-a-call">
            <CalendarCheck className="mr-2 h-4 w-4" /> Pick a Time on Our Calendar
          </Link>
        </Button>
        <Button asChild variant="outline" className="mt-2 w-full">
          <a href="tel:+13054337789"><Phone className="mr-2 h-4 w-4" />Or call now: (305) 433-7789</a>
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl bg-white p-6 md:p-7 shadow-2xl">
      <div className="mb-5">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" /> Specialists available today
        </span>
        <h3 className="mt-3 text-2xl font-bold leading-tight text-secondary">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="space-y-3.5">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="hcf-fn" className="text-xs font-semibold text-secondary">First name *</Label>
            <Input id="hcf-fn" value={form.first_name} autoComplete="given-name" placeholder="First name" onChange={(e) => set("first_name", e.target.value)} required />
          </div>
          <div className="space-y-1">
            <Label htmlFor="hcf-ph" className="text-xs font-semibold text-secondary">Phone *</Label>
            <Input id="hcf-ph" type="tel" value={form.phone} autoComplete="tel" placeholder="(555) 555-5555" onChange={(e) => set("phone", e.target.value)} required />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="hcf-em" className="text-xs font-semibold text-secondary">Work email *</Label>
          <Input id="hcf-em" type="email" value={form.email} autoComplete="email" placeholder="you@company.com" onChange={(e) => set("email", e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-secondary">Project size</Label>
            <Select value={form.estimated_budget} onValueChange={(v) => set("estimated_budget", v)}>
              <SelectTrigger className="text-sm"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {projectSizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-secondary">What do you need?</Label>
            <Select value={form.interest} onValueChange={(v) => set("interest", v)}>
              <SelectTrigger className="text-sm"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {interests.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
                {/* Pre-call context: a sentence or two so the specialist calls prepared */}
        <div className="space-y-1">
          <Label htmlFor="hcf-desc" className="text-xs font-semibold text-secondary">Briefly describe your project</Label>
          <Textarea
            id="hcf-desc"
            value={form.project_description}
            onChange={(e) => set("project_description", e.target.value)}
            rows={2}
            className="text-sm resize-none"
            placeholder="Optional: what you store, building size, timeline…"
          />
        </div>
        <Button
          type="submit"
          size="lg"
          className="w-full py-6 text-base font-bold shadow-lg shadow-primary/30 transition-all hover:shadow-xl hover:shadow-primary/40 hover:scale-[1.01]"
          disabled={!valid || submitting}
        >
          {submitting ? "Sending…" : (<>Get My Free Call <ArrowRight className="ml-1.5 h-5 w-5" /></>)}
        </Button>
      </div>
      <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-primary" /> 1 business day</span>
        <span className="inline-flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5 text-primary" /> Free estimate</span>
        <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> No obligation</span>
      </div>
      <p className="mt-3 text-center text-xs text-muted-foreground">
        Prefer to schedule it yourself?{" "}
        <Link to="/book-a-call" className="font-semibold text-primary underline-offset-2 hover:underline">Pick a time on our calendar</Link>
      </p>
    </form>
  );
};

export default HeroCallbackForm;
